import { useCallback, useEffect, useRef, useReducer } from "react";
import { createInitialToolCallState, toolCallReducer } from "../domain/toolCallMachine";
import type { EngineInput, ScenarioDefinition } from "../domain/types";
import { ToolCallWorkerClient } from "../worker/workerClient";

/**
 * Conecta la máquina de estados pura (domain/toolCallMachine.ts) con el Web Worker.
 * Ningún dato del payload se registra aquí; solo se reenvía al worker y de vuelta al
 * reducer (08-seguridad-privacidad.md: nunca loguear payloads).
 */
export function useToolCallRun(scenario: ScenarioDefinition) {
  const [state, dispatch] = useReducer(
    toolCallReducer,
    scenario,
    createInitialToolCallState,
  );
  const clientRef = useRef<ToolCallWorkerClient | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);
  const pendingInputRef = useRef<EngineInput | null>(null);
  const runCounter = useRef(0);

  useEffect(() => {
    clientRef.current = new ToolCallWorkerClient();
    return () => {
      clientRef.current?.dispose();
      clientRef.current = null;
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET", scenario });
    pendingInputRef.current = null;
    cancelRef.current = null;
  }, [scenario]);

  const execute = useCallback(
    (input: EngineInput) => {
      const client = clientRef.current;
      if (!client) return;
      runCounter.current += 1;
      const runId = `run_${scenario.id}_${runCounter.current}`;
      const { promise, cancel } = client.run(runId, input, (value) => {
        dispatch({ type: "PROGRESS", value });
      });
      cancelRef.current = cancel;
      promise.then((result) => {
        cancelRef.current = null;
        if (result.ok) {
          dispatch({ type: "SUCCESS", output: result.output });
          return;
        }
        if (result.error.code === "RUN_CANCELLED") {
          dispatch({ type: "CANCEL" });
          return;
        }
        dispatch({ type: "FAILURE", error: result.error });
      });
    },
    [scenario.id],
  );

  useEffect(() => {
    if (state.status !== "running") return;
    if (state.progress !== 0) return; // ya hay una ejecución en curso para este ciclo
    const input = pendingInputRef.current;
    if (!input) return;
    execute(input);
  }, [state.status, state.progress, execute]);

  return {
    state,
    request: (input: EngineInput) => {
      pendingInputRef.current = input;
      dispatch({ type: "REQUEST" });
    },
    grantPermission: () => dispatch({ type: "GRANT_PERMISSION" }),
    denyPermission: () => dispatch({ type: "DENY_PERMISSION" }),
    confirm: () => dispatch({ type: "CONFIRM" }),
    cancelConfirmation: () => dispatch({ type: "CANCEL_CONFIRMATION" }),
    cancelRun: () => cancelRef.current?.(),
    reset: () => {
      pendingInputRef.current = null;
      dispatch({ type: "RESET", scenario });
    },
  };
}
